import { Box, Button, Drawer, Flex, Select, Skeleton, Table, Text } from "@mantine/core";
import { Page, PageHeader } from "../../components/layout/Page";
import { CheckCircleIcon, InfoIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSwr from "swr";
import { LedgerApi } from "../../apis";
import { IMirrorGroupDetail, IMirrorGroupEntry, IMirrorResponse } from "../../apis/ledger";
import { DateRangeChip, ListFooter, ListToolbar, MoneyCell } from "../../components/list";
import { Notice } from "../../components/ui/notice";
import { StatusBadge } from "../../components/ui/status-badge";
import classes from "./ledger-table.module.css";
import mirrorClasses from "./Mirror.module.css";
import { ListStates, resolveListStates } from "../../components/list";

/**
 * Хосолсон бичилт (SPRINT-06 §8.1).
 *
 * Хоёр зүйлийг ил гаргах нь энэ дэлгэцийн гол зорилго:
 *
 *   • НЭГ үйл явдал ХОЁР дэвтэрт бичигдсэнийг зэрэгцүүлж харуулах. Хоёр
 *     компанийн бичилт нэг дэлгэц дээр зэрэгцэж байж л «тэнцсэн» гэдэг нь
 *     шалгаж болох мэдэгдэл болно.
 *   • MEMO тал нь балансын ГАДУУР. Түүнийг ердийн хөлтэй нийлүүлбэл
 *     эзэмшигчийн балансад өөрийнх нь биш хөрөнгө суусан мэт харагдана —
 *     энэ бүтээгдэхүүн яг үүнээс зайлсхийхээр бүтээгдсэн (§0.2).
 *
 * DEV дээр хоосон байх нь ХЭВИЙН: `postMirror`-ыг дуудах production зам нь
 * Алхам 8 (event wiring) бөгөөд §10 Q0b-г хүлээж байна. Тиймээс хоосон
 * төлөв нь «алдаа» биш, «хосолсон бичилт хараахан үүсээгүй» гэж хэлнэ.
 */

const EVENTS = [
  "CONSIGN_DEPOSITED",
  "CONSIGN_DEPOSIT_RECEIVED",
  "CONSIGN_SOLD",
  "CONSIGN_COMMISSION",
  "CONSIGN_SETTLED",
  "CONSIGN_RETURNED",
  "CONSIGN_SHRINKAGE_RETAILER",
  "CONSIGN_SHRINKAGE_SUPPLIER",
  "RESALE_INTERNAL_TRANSFER",
];

const COLS = 6;

/** Нэг талын бичилт — гарчиг, мөрүүд, нийт. */
const SideCard = ({ side }: { side: IMirrorGroupEntry }) => {
  const { t } = useTranslation(["billing"]);

  return (
    <Box className={side.isMemo ? mirrorClasses.sideCardMemo : mirrorClasses.sideCard}>
      <Flex align="center" gap={6} mb={6} wrap="wrap">
        <Text size="sm" fw={500}>{side.businessName}</Text>
        <Text size="sm" ff="monospace" fw={500} className={classes.code}>{side.entryNumber}</Text>
        {side.isMemo && (
          <StatusBadge color="gray" label={t("billing:ledger.mirror.offBalance")} />
        )}
      </Flex>
      <Text size="sm" c="dimmed" mb={6}>{side.postingTemplate || "-"}</Text>

      <Box className={classes.scroll}>
        <Table className={classes.table} highlightOnHover verticalSpacing="sm" horizontalSpacing="md" withRowBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={classes.th}>{t("billing:ledger.colAccount")}</Table.Th>
              <Table.Th className={classes.th}>{t("billing:ledger.colName")}</Table.Th>
              <Table.Th className={classes.th} ta="right">{t("billing:ledger.colDebit")}</Table.Th>
              <Table.Th className={classes.th} ta="right">{t("billing:ledger.colCredit")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {side.lines.map((line) => (
              <Table.Tr key={line.id}>
                <Table.Td>
                  <Text size="sm" ff="monospace" fw={500} className={classes.code}>{line.accountCode}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500}>{line.accountName}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <MoneyCell amount={line.isDebit ? line.amount : null} />
                </Table.Td>
                <Table.Td ta="right">
                  <MoneyCell amount={line.isDebit ? null : line.amount} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export const LedgerMirrorPage = () => {
  const { t } = useTranslation(["billing", "common"]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [range, setRange] = useState<[string | null, string | null]>([null, null]);
  const [event, setEvent] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const [dateFrom, dateTo] = range;
  const key = `ledger.mirror.${page}.${dateFrom ?? ""}.${dateTo ?? ""}.${event ?? ""}.${limit}`;

  const { data, isLoading, error } = useSwr<IMirrorResponse>(
    key,
    async () => await LedgerApi.getMirrorGroups({
      page,
      limit,
      ...(dateFrom ? { dateFrom: dayjs(dateFrom).format("YYYY-MM-DD") } : {}),
      ...(dateTo ? { dateTo: dayjs(dateTo).format("YYYY-MM-DD") } : {}),
      ...(event ? { event } : {}),
    }),
    { revalidateOnFocus: false },
  );

  const { data: detail } = useSwr<IMirrorGroupDetail>(
    openId ? `ledger.mirror.detail.${openId}` : null,
    async () => await LedgerApi.getMirrorGroup(openId!),
    { revalidateOnFocus: false },
  );

  const groups = data?.groups ?? [];
  const hasFilters = !!(dateFrom || dateTo || event);
  const clearFilters = () => { setRange([null, null]); setEvent(null); setPage(1); };
  const total = data?.pagination.total ?? 0;

  // Балансын хөл ба memo хөлийг ЗААВАЛ тусад нь — §8.1-ийн «балансын гадуур»
  // зураас нь энэ бүтээгдэхүүний гол ялгаа.
  const balanceSides = detail?.sides.filter((s) => !s.isMemo) ?? [];
  const memoSides = detail?.sides.filter((s) => s.isMemo) ?? [];

  const listStates = resolveListStates(
    <ListStates
      loading={isLoading}
      error={error}
      empty={groups.length === 0}
      filtered={hasFilters}
      onClear={clearFilters}
      columns={COLS}
      emptyTitle={t("billing:ledger.mirror.emptyTitle")}
      emptyDescription={t("billing:ledger.mirror.emptyHint")}
    />
  );

  return (
    <Page>
      <PageHeader
        title={
          <>
            {t("billing:ledger.mirror.title")}
            {total > 0 && <Text span className={mirrorClasses.titleChip}>{total}</Text>}
          </>
        }
        subtitle={t("billing:ledger.mirror.subtitle")}
      />

      {error ? (
        <Notice tone="danger" role="alert">{t("billing:ledger.mirror.loadFailed")}</Notice>
      ) : null}

      <Box className={mirrorClasses.card}>
        <ListToolbar
          filterSlot={
            <>
              <DateRangeChip
                label={t("billing:ledger.datePlaceholder")}
                value={range}
                onChange={(value) => { setRange(value); setPage(1); }}
              />
              <Select
                value={event}
                onChange={(value) => { setEvent(value); setPage(1); }}
                data={EVENTS.map((e) => ({
                  value: e,
                  label: t(`billing:ledger.mirror.event.${e}`, { defaultValue: e }),
                }))}
                placeholder={t("billing:ledger.mirror.colEvent")}
                size="sm"
                radius="md"
                clearable
                aria-label={t("billing:ledger.mirror.colEvent")}
                w={240}
              />
            </>
          }
        />

        <Box className={mirrorClasses.wrap}>
          {listStates ?? (
          <Table className={mirrorClasses.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("billing:ledger.colDate")}</Table.Th>
                <Table.Th>{t("billing:ledger.mirror.colEvent")}</Table.Th>
                <Table.Th>{t("billing:ledger.colNumber")}</Table.Th>
                <Table.Th>{t("billing:ledger.mirror.colParties")}</Table.Th>
                <Table.Th ta="right">{t("billing:ledger.mirror.colAmount")}</Table.Th>
                <Table.Th>{t("billing:ledger.mirror.colSides")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {groups.map((g) => (
                  <Table.Tr
                    key={g.mirrorGroupId}
                    className={mirrorClasses.row}
                    tabIndex={0}
                    role="button"
                    aria-label={g.sides.map((s) => s.entryNumber).join(", ")}
                    onClick={() => setOpenId(g.mirrorGroupId)}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        setOpenId(g.mirrorGroupId);
                      }
                    }}>
                    <Table.Td>
                      <Text span className={mirrorClasses.strong}>{dayjs(g.entryDate).format("YYYY-MM-DD")}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text span className={mirrorClasses.strong}>
                        {t(`billing:ledger.mirror.event.${g.event}`, { defaultValue: g.event ?? "-" })}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text span className={mirrorClasses.docLink}>
                        {g.sides.map((s) => s.entryNumber).join(" · ") || "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text span className={mirrorClasses.parties}>
                        {g.parties.map((p) => p.businessName).join(" - ")}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <MoneyCell amount={g.amount} />
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge
                        color={!g.isBalanced ? "red" : g.sideCount < 2 ? "yellow" : "green"}
                        uppercase={false}
                        label={
                          g.sideCount < 2
                            ? t("billing:ledger.mirror.oneSided")
                            : t("billing:ledger.mirror.sidesCount", { count: g.sideCount })
                        }
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
          </Table>
          )}
        </Box>

        {groups.length > 0 && (
          <Box className={mirrorClasses.hint}>
            <InfoIcon size={14} weight="bold" className={mirrorClasses.hintIcon} />
            <Text span>{t("billing:ledger.mirror.oneSidedHint")}</Text>
          </Box>
        )}

        {total > 0 && (
          <ListFooter
            from={Math.min(total, (page - 1) * limit + 1)}
            to={Math.min(total, (page - 1) * limit + groups.length)}
            total={total}
            page={page}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setLimit(size); setPage(1); }}
            pageSizeOptions={["20", "50"]}
          />
        )}
      </Box>

      <Drawer
        opened={openId !== null}
        onClose={() => setOpenId(null)}
        size="lg"
        title={detail
          ? `${t(`billing:ledger.mirror.event.${detail.event}`, { defaultValue: detail.event ?? "" })} · ${dayjs(detail.entryDate).format("YYYY-MM-DD")}`
          : ""}>
        {detail && (
          <Box>
            <Text size="sm" c="dimmed" mb="md">
              {t("billing:ledger.mirror.detailMeta", {
                source: t(`billing:ledger.source.${detail.sourceType}`, { defaultValue: detail.sourceType }),
                ref: detail.sourceId?.slice(0, 8) ?? "-",
              })}
            </Text>

            <Box className={mirrorClasses.sideGrid}>
              {balanceSides.map((side) => <SideCard key={side.entryId} side={side} />)}
            </Box>

            {memoSides.length > 0 && (
              <Box mt="md">
                <Flex align="center" gap={6} mb={6}>
                  <Text size="sm" fw={500}>{t("billing:ledger.mirror.offBalanceHeading")}</Text>
                </Flex>
                <Text size="sm" c="dimmed" mb={6}>{t("billing:ledger.mirror.offBalanceNote")}</Text>
                <Box className={mirrorClasses.sideGrid}>
                  {memoSides.map((side) => <SideCard key={side.entryId} side={side} />)}
                </Box>
              </Box>
            )}

            <Box mt="md">
              {detail.isBalanced ? (
                <Box className={mirrorClasses.balanced}>
                  <CheckCircleIcon size={18} weight="bold" />
                  <Text span>{t("billing:ledger.mirror.balancedNote", { count: detail.sideCount })}</Text>
                </Box>
              ) : (
                <Notice tone="danger" role="alert">{t("billing:ledger.mirror.unbalancedNote")}</Notice>
              )}
            </Box>

            {detail.sideCount < 2 && (
              <Box mt="sm">
                <Notice>{t("billing:ledger.mirror.oneSidedNote")}</Notice>
              </Box>
            )}
          </Box>
        )}
      </Drawer>
    </Page>
  );
};

export default LedgerMirrorPage;
